import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Track, setTrackReaction, loadDb } from "../data/schema";
import { showToast } from "components/ui-kit/Toast";
import LikeIcon from "../../../assets/like.svg";
import DislikeIcon from "../../../assets/dislike.svg";
import ExportIcon from "../../../assets/export.svg";
import PlayIcon from "../../../assets/play-player.svg";
import { DeviceSize } from "constants/deviceSize";

const AUDIO_LOAD_START_EVENT = 'trackAudioLoadStart';
const AUDIO_LOAD_END_EVENT = 'trackAudioLoadEnd';

type Props = {
  track: Track | null;
  onClose: () => void;
  hasPlayer?: boolean;
  onPlayTrack?: (track: Track) => void;
  playingTrack?: Track | null;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  source?: 'view' | 'library'; // Track where the modal was opened from
};

export const TrackModal: React.FC<Props> = ({ track, onClose, hasPlayer = false, onPlayTrack, playingTrack, isPlaying = false, onTogglePlay, source = 'view' }) => {
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(track?.reaction || null);
  const [isClosing, setIsClosing] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  
  // Update reaction when track changes - load from database
  useEffect(() => {
    if (!track) return;
    
    const db = loadDb();
    const dbTrack = db.tracks[track.id];
    if (dbTrack) {
      setReaction(dbTrack.reaction || null);
    } else {
      setReaction(track.reaction || null);
    }
  }, [track?.id]);

  // Mark body when TrackModal is open so other components (e.g. TrackMenu) can adjust layout.
  // Для Library ставим отдельный класс, чтобы сабменю там всегда открывались слева.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    if (source === 'library') {
      body.classList.add('track-modal-open-library');
    } else {
      body.classList.add('track-modal-open');
    }
    return () => {
      if (source === 'library') {
        body.classList.remove('track-modal-open-library');
      } else {
        body.classList.remove('track-modal-open');
      }
    };
  }, [source]);
  
  // Listen for reaction changes from outside
  useEffect(() => {
    if (!track) return;
    
    const handleReactionChanged = (e: CustomEvent) => {
      if (e.detail.trackId === track.id) {
        setReaction(e.detail.reaction);
      }
    };
    
    window.addEventListener('trackReactionChanged', handleReactionChanged as EventListener);
    return () => window.removeEventListener('trackReactionChanged', handleReactionChanged as EventListener);
  }, [track?.id]);

  // Reset closing state when track changes
  useEffect(() => {
    if (track) {
      setIsClosing(false);
      if (typeof window !== 'undefined') {
        const globalSet: Set<string> | undefined = (window as any).__audioLoadingTrackIds;
        setIsAudioLoading(Boolean(globalSet?.has(track.id)));
      } else {
        setIsAudioLoading(false);
      }
    }
  }, [track?.id]);

  useEffect(() => {
    if (!track) return;
    const handleAudioLoadStart = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (detail?.trackId === track.id) {
        setIsAudioLoading(true);
      }
    };
    const handleAudioLoadEnd = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (detail?.trackId === track.id) {
        setIsAudioLoading(false);
      }
    };
    window.addEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
    window.addEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    return () => {
      window.removeEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
      window.removeEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    };
  }, [track?.id]);

  const artworkUrl =
    track?.params?.localImageUrl ||
    track?.params?.imageUrl ||
    null;

  if (!track) return null;

  const params = (track.params || {}) as Record<string, any>;
  const isCustomMode =
    params.customMode === true ||
    params.mode === 'custom';

  // Description: берём самое актуальное, в зависимости от режима
  let description = '';
  if (isCustomMode) {
    description = (
      track.description ??
      params.prompt ??
      params.songDescription ??
      ''
    ).toString();
  } else {
    description = (
      params.songDescription ??
      track.description ??
      params.prompt ??
      ''
    ).toString();
  }

  // Styles: сначала stylesDescription, потом style / tags
  let styles = (
    params.stylesDescription ??
    params.style ??
    params.tags ??
    ''
  ).toString();

  const lyrics = params.lyricsText || '';
  const likeCount = reaction === 'like' ? 1 : 0;
  const dislikeCount = reaction === 'dislike' ? 1 : 0;

  const handleReaction = async (type: 'like' | 'dislike') => {
    const next = reaction === type ? null : type;
    setReaction(next);
    try {
      await setTrackReaction(track.id, next);
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
        detail: { trackId: track.id, reaction: next } 
      }));
    } catch (error) {
      console.error('Failed to set track reaction:', error);
      showToast('Failed to update reaction', undefined, 'error');
      // Revert reaction on error
      setReaction(reaction);
    }
  };
  
  const handlePlay = () => {
    if (!track || isAudioLoading) return;
    
    const isThisTrack = playingTrack?.id === track.id;
    if (isThisTrack && onTogglePlay) {
      // Если это текущий трек (играет или на паузе), переключаем play/pause
      onTogglePlay();
    } else if (onPlayTrack) {
      // Если другой трек, запускаем его
      onPlayTrack(track);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 100); // Match animation duration
  };

  return (
    <ModalOverlay $source={source} $hasPlayer={hasPlayer}>
      <ModalSheet $hasPlayer={hasPlayer} $source={source} $isClosing={isClosing}>
        <CloseButton onClick={handleClose}>×</CloseButton>
        <Artwork>
          <ArtworkImage $imageUrl={artworkUrl} />
        </Artwork>
        <Stats>
          <StatItem $clickable={true} onClick={handlePlay}>
            <StatIconWrapper $isPlayIcon={true} $loading={isAudioLoading}>
              {isAudioLoading ? (
                <LoaderSpinner />
              ) : playingTrack?.id === track.id && isPlaying ? (
                <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: 'currentColor' }}>
                  <rect x="6" y="4" width="4" height="16" fill="currentColor" />
                  <rect x="14" y="4" width="4" height="16" fill="currentColor" />
                </svg>
              ) : (
                <PlayIcon />
              )}
            </StatIconWrapper>
            <StatValue>1</StatValue>
          </StatItem>
          <StatItem $clickable={true} onClick={() => handleReaction('like')}>
            <StatIconWrapper $active={reaction === 'like'}>
              <LikeIcon />
            </StatIconWrapper>
            <StatValue>{likeCount}</StatValue>
          </StatItem>
          <StatItem $clickable={true} onClick={() => handleReaction('dislike')}>
            <StatIconWrapper $active={reaction === 'dislike'}>
              <DislikeIcon />
            </StatIconWrapper>
            <StatValue>{dislikeCount}</StatValue>
          </StatItem>
        </Stats>
        <Title>{track.title || 'Song'}</Title>
       
        <StylesSection>
          <StylesTitle>Styles</StylesTitle>
          <StylesContent>{styles || '(No styles)'}</StylesContent>
        </StylesSection>
        <StylesSection>
          <StylesContent>{description || '(No description)'}</StylesContent>
        </StylesSection>
        {lyrics && (
          <LyricsSection>
            {lyrics.split('\n\n').map((section: string, i: number) => {
              const lines = section.split('\n');
              const header = lines[0].match(/^\[(.+)\]$/)?.[1];
              const content = header ? lines.slice(1) : lines;
              return (
                <LyricsBlock key={i}>
                  {header && <LyricsHeader>[{header}]</LyricsHeader>}
                  {content.map((line: string, j: number) => (
                    <LyricsLine key={j}>{line}</LyricsLine>
                  ))}
                </LyricsBlock>
              );
            })}
          </LyricsSection>
        )}
      </ModalSheet>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div<{ $hasPlayer?: boolean; $source?: 'view' | 'library' }>`
  position: ${p => p.$source === 'library' ? 'absolute' : 'fixed'};
  inset: 0;
  z-index: 40;
  pointer-events: none;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    justify-content: center;
    align-items: flex-end;
  }
`;

const ModalSheet = styled.div<{ $hasPlayer?: boolean; $source?: 'view' | 'library'; $isClosing?: boolean }>`
  pointer-events: auto;
  background: #fff;
  padding: 20px 24px 24px 24px;
  box-shadow: 0 0 20px rgba(15, 23, 42, 0.25);
  overflow-y: auto;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    box-shadow: none;

    width: 100%;
    max-height: calc(100% - 40px);
    min-height: 60vh;
    border-radius: 24px 24px 0 0;
    padding-bottom: ${p => p.$hasPlayer ? 'calc(24px + 15vh)' : '24px'};
    transform: ${p => p.$isClosing ? 'translateY(100%)' : 'translateY(0)'};
    transition: transform 0.3s ease;
    animation: ${p => p.$isClosing ? 'none' : 'slideUpTrackModal 0.3s ease'};
    @keyframes slideUpTrackModal {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  }

  @media (min-width: ${DeviceSize.TabletMedium}) {

    position: absolute;
    top: 16px;
    right: 0;
    width: ${p => p.$source === 'library' ? '30%' : '40%'};
    max-height: ${p =>
      p.$hasPlayer
        ? 'calc(100% - 130px)'
        : 'calc(100% - 32px)'};
    height: auto;
    min-height: 65vh;
    border-radius: 12px;
    margin-right: 20px;
    transform: ${p => p.$isClosing ? 'translateX(100%)' : 'translateX(0)'};
    transition: transform 0.3s ease, max-height 0.3s ease, height 0.3s ease, width 0.3s ease;
    animation: ${p => p.$isClosing ? 'none' : 'slideInFromRight 0.3s ease'};

    @keyframes slideInFromRight {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 22px;
  height: 22px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.04);
  color: #6b7280;
  border: none;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Artwork = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ArtworkImage = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 100%;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const StatItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
`;

const StatIconWrapper = styled.div<{ $active?: boolean; $isPlayIcon?: boolean; $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => {
    if (p.$isPlayIcon) return '#6777FF';
    return p.$active ? '#6777FF' : '#9aa0a6';
  }};
  svg {
    width: 20px;
    height: 20px;
  }
  svg,
  svg * {
    fill: currentColor;
  }
`;

const LoaderSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 3px solid rgba(103, 119, 255, 0.25);
  border-top-color: #6777FF;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const StatValue = styled.span`
  font-size: 14px;
  color: #666;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: 3.36em;
`;

const StylesSection = styled.div`
  margin-bottom: 20px;
  padding: 12px 14px;
  border: none;
  border-radius: 8px;
  background: #f3f4f6;
`;

const StylesTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
`;

const StylesContent = styled.div`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
`;

const LyricsSection = styled.div`
  margin-bottom: 20px;
`;

const LyricsBlock = styled.div`
  margin-bottom: 16px;
`;

const LyricsHeader = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
`;

const LyricsLine = styled.div`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 4px;
`;

export default TrackModal;

